import { computed } from 'vue';
import { useMeQuery } from '@/composables/useMeQuery';
import { useMainStore } from '@/stores/mainStore';

export interface TeamOption {
  label: string;
  value: string;
}

export function useTeams() {
  const { data: userData } = useMeQuery();

  // Teams formatted for select components (label/value format)
  const teams = computed<TeamOption[]>(() => {
    return userData.value?.teams.map((team) => ({
      label: team.name,
      value: team.twrn,
    })).sort((a, b) => a.label.localeCompare(b.label)) ?? [];
  });

  // Raw teams data
  const rawTeams = computed(() => userData.value?.teams ?? []);

  // Get team name by TWRN
  const getTeamNameByTwrn = (twrn: string): string | undefined => {
    return userData.value?.teams.find((team) => team.twrn === twrn)?.name;
  };

  // Get full team data by TWRN
  const getTeamByTwrn = (twrn: string) => {
    return userData.value?.teams.find((team) => team.twrn === twrn);
  };

  // Check if user has access to a specific team
  const hasTeamAccess = (twrn: string): boolean => {
    return userData.value?.teams.some((team) => team.twrn === twrn) ?? false;
  };

  // Get team names as array
  const teamNames = computed(() => {
    return userData.value?.teams.map((team) => team.name).sort() ?? [];
  });

  // Get team TWRNs as array
  const teamTwrns = computed(() => {
    return userData.value?.teams.map((team) => team.twrn) ?? [];
  });

  // Get the current team the user is in by mapping the teamTwrn to the team name
  const currentTeam = computed(() => {
    return userData.value?.teams.find((team) => team.twrn === useMainStore().teamTwrn) ?? null;
  });

  return {
    teams,
    rawTeams,
    teamNames,
    teamTwrns,
    getTeamNameByTwrn,
    getTeamByTwrn,
    hasTeamAccess,
    currentTeam,
  };
}
